// @ts-nocheck

import memoize from 'memoize-state';

import { type StateType } from '../../../states';
import Grid from '../states/grid';
import Pool from '../states/pool';
import { PublicGridFilters, GridMatchLengths } from '../types';
import type { GridState, Contributor, PublicGridFilter, GridMatchLength } from '../types';

import { getGrids, getIsProgressBarFilled } from './grids';
import { getPool, getPublicGridsReferences } from './pool';

export const getPoolId = (state: StateType): number | null | undefined => {
  const { poolId } = state.grids.publicGrids;
  return poolId;
};

export const getFilter = (state: StateType): PublicGridFilter => {
  const { filter } = state.grids.publicGrids;
  return filter;
};

export const getCurrentPage = (state: StateType): number => {
  const { currentPage } = state.grids.publicGrids;
  return currentPage;
};

export const getPublicGrids = (state: StateType, poolId: number | null): Array<GridState> | null => {
  const references = getPublicGridsReferences(state, poolId);
  const grids = getGrids(state);
  return references?.map((ref) => grids[ref]).filter((k) => k);
};

export const getGridsCount = (state: StateType, poolId: number | null): number => {
  const references = getPublicGridsReferences(state, poolId);
  return references?.length || 0;
};

export const getGridContributors = (grid: GridState | null): Array<Contributor> => {
  if (grid) {
    const { betslips } = grid;
    return betslips?.map<Contributor>((betslip) => {
      const { nickname, avatarPath, validationDate, betAmount } = betslip;
      return {
        nickname,
        avatarPath,
        date: validationDate,
        amount: betAmount,
      };
    });
  }
  return [];
};

export const getSortedGrids = (state: StateType, poolId: number | null): Array<GridState> => {
  const filter = getFilter(state);
  const grids = getPublicGrids(state, poolId);
  const type = poolId ? Pool.getType(poolId) : null;
  return computeSortedGrids(grids, filter, type);
};

export const getCurrentSortedGridReference = (state: StateType, poolId: number | null): string | null => {
  const grid = getCurrentSortedGrid(state, poolId);
  return grid?.gridReference || null;
};

export const getPageIndexOnSortedGrid = (
  state: StateType,
  poolId: number | null,
  gridReference: string | null,
): number => {
  if (poolId && gridReference) {
    const grids = getSortedGrids(state, poolId);
    return grids.map((grid) => grid.gridReference).indexOf(gridReference);
  }
  return -1;
};

export const getCurrentSortedGrid = (state: StateType, poolId: number | null): GridState | null => {
  const grids = getSortedGrids(state, poolId);
  const currentPage = getCurrentPage(state);
  return grids[currentPage] || null;
};

export const computeSortedGrids = memoize(
  (grids: Array<GridState> | null, filter: PublicGridFilter, type: GridMatchLength | null): Array<GridState> => {
    if (Array.isArray(grids) && grids.length) {
      return grids
        .map((k) => k)
        .sort((a, b) => {
          switch (filter) {
            default:
            case PublicGridFilters.MOST_COMPLETE: {
              const completenessA = a.allBetAmount / Grid.getAmount(a);
              const completenessB = b.allBetAmount / Grid.getAmount(b);
              return completenessA > completenessB ? -1 : 1;
            }
            case PublicGridFilters.MOST_EXPENSIVE: {
              return Grid.getAmount(a) > Grid.getAmount(b) ? -1 : 1;
            }
            case PublicGridFilters.LESS_EXPENSIVE: {
              return Grid.getAmount(a) > Grid.getAmount(b) ? 1 : -1;
            }
            case PublicGridFilters.BEST_AUTHOR_WINNINGS: {
              const { winnings: winningsA } = a.author;
              const { winnings: winningsB } = b.author;
              const winningsAseven = (winningsA && winningsA[GridMatchLengths.SEVEN]) || 0;
              const winningsBseven = (winningsB && winningsB[GridMatchLengths.SEVEN]) || 0;
              const winningsAtwelve = (winningsA && winningsA[GridMatchLengths.TWELVE]) || 0;
              const winningsBtwelve = (winningsB && winningsB[GridMatchLengths.TWELVE]) || 0;
              if (type === GridMatchLengths.SEVEN) {
                return winningsAseven > winningsBseven ? -1 : 1;
              }
              if (type === GridMatchLengths.TWELVE) {
                return winningsAtwelve > winningsBtwelve ? -1 : 1;
              }
              const allWinningsA = winningsAseven + winningsAtwelve;
              const allWinningsB = winningsBseven + winningsBtwelve;
              return allWinningsA > allWinningsB ? -1 : 1;
            }
            case PublicGridFilters.MOST_RECENT: {
              const dateA = a.betslips[0].validationDate;
              const dateB = b.betslips[0].validationDate;
              return dateA > dateB ? -1 : 1;
            }
            case PublicGridFilters.BEST_AUTHOR_BADGES: {
              if (type) {
                const bestBadgeA = Grid.getBestAuthorBadge(a, type) || 0;
                const bestBadgeB = Grid.getBestAuthorBadge(b, type) || 0;
                return bestBadgeA > bestBadgeB ? -1 : 1;
              }
              const nbBadgesA = a.author.badges?.length || 0;
              const nbBadgesB = b.author.badges?.length || 0;
              return nbBadgesA > nbBadgesB ? -1 : 1;
            }
            case PublicGridFilters.MOST_POPULAR: {
              const nbBetslipsA = a.betslips.length;
              const nbBetslipsB = b.betslips.length;
              return nbBetslipsA > nbBetslipsB ? -1 : 1;
            }
          }
        });
    }
    return [];
  },
);

export const getPublicGridsList = (state: StateType) => {
  const currentPage = getCurrentPage(state);
  const poolId = getPoolId(state);

  let currentGridReference = null;
  let currentGridIsSystem = null;
  let gridDate = null;

  const sortedGrids = getSortedGrids(state, poolId);
  if (sortedGrids[currentPage]) {
    currentGridReference = sortedGrids[currentPage].gridReference;
    currentGridIsSystem = !!sortedGrids[currentPage].isSystem;
    gridDate = Grid.getDate(sortedGrids[currentPage]);
  }

  return {
    grids: sortedGrids,
    currentPage,
    gridReference: currentGridReference,
    isSystem: currentGridIsSystem,
    poolId,
    gridDate,
  };
};

export const publicGridButtonSelector = (state: StateType, { poolId }: { poolId: number }) => {
  const poolNumber = Pool.getNumber(poolId);
  const poolType = Pool.getType(poolId);
  const linkPool = Pool.getLink(poolType, poolNumber);
  const publicLink = `${linkPool}/public`;
  const pool = getPool(state, poolId);
  const publicGridsCount = pool?.nbPublicGrids;
  return { publicLink, publicGridsCount };
};

export const getPublicCta = (state: StateType, { gridReference }: { gridReference: string | null }) => {
  return { isProgressBarFilled: getIsProgressBarFilled(state, gridReference) };
};
